import { FC, useState } from 'react';
import styled from 'styled-components';

import { CloseButton } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { Input, RadioCircle, RadioInput } from '@/components/Forms';
import { Modal } from '@/components/Modal';
import { SidePanelFooter } from '@/components/SidePanel';
import Text from '@/components/Text';

import { ImageData } from '../VariationOne';
import {
  ActionBox,
  ActionTitle,
  Dialog,
  ModalContent,
  ModalHeader,
  StyledRadiosLabel,
} from '../shared/styledComponents';

const InputsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 24px;
`;

const IMAGE_POSITION_VALUES = ['left', 'right', 'custom'];

interface Props {
  image: ImageData;
  imageIndex: number;
  onClose: () => void;
  setImages: React.Dispatch<React.SetStateAction<ImageData[]>>;
}

const SettingsModal: FC<Props> = ({
  onClose,
  image,
  setImages,
  imageIndex,
}) => {
  const [imagePosition, setImagePosition] = useState(image.imagePosition || 'right');
  const [imagePositionLeft, setImagePositionLeft] = useState(
    image.imagePositionLeft ?? (image.imagePosition === 'left' ? 40 : image.imagePosition === 'right' ? 40 : undefined),
  );
  const [imagePositionTop, setImagePositionTop] = useState(
    image.imagePositionTop ?? -536,
  );

  const handlePositionChange = (position: string) => {
    setImagePosition(position);
    // Set standard positions when left or right is selected
    if (position === 'left') {
      setImagePositionLeft(40);
      setImagePositionTop(-536);
    } else if (position === 'right') {
      setImagePositionLeft(40); // Same as left, but will be applied as 'right' in CSS
      setImagePositionTop(-536);
    } else if (position === 'custom') {
      // For custom, don't set default values - let user specify their own
      // Custom positioning uses absolute coordinates, different from standard positions
    }
  };

  const onUpdateSettings = () => {
    setImages((prev) => {
      const newArray = [...prev];
      newArray[imageIndex] = {
        ...image,
        imagePosition,
        imagePositionLeft,
        imagePositionTop,
      };
      return newArray;
    });
    onClose();
  };

  return (
    <Modal zIndex={1}>
      <Dialog
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.5 }}
      >
        <ModalHeader>
          <Text $type="button">Image Parameters</Text>
          <CloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CloseButton>
        </ModalHeader>
        <ModalContent>
          <div>
            <ActionTitle $type="smallHeadline">Image Position</ActionTitle>
            <ActionBox>
              {IMAGE_POSITION_VALUES.map((value) => (
                <StyledRadiosLabel key={value}>
                  {value}
                  <RadioInput
                    type="radio"
                    name="actual_photo"
                    checked={imagePosition === value}
                    onChange={() => handlePositionChange(value)}
                  />
                  <RadioCircle />
                </StyledRadiosLabel>
              ))}
            </ActionBox>
          </div>
          <InputsGrid>
            <div>
              <ActionTitle $type="smallHeadline">
                Horizontal Position (From {imagePosition === 'left' ? 'left' : 'right'})
              </ActionTitle>
              <Input
                disabled={imagePosition !== 'custom'}
                value={imagePositionLeft ?? ''}
                onChange={(e) => setImagePositionLeft(parseInt(e.target.value))}
                placeholder="40 px"
                type="number"
              />
            </div>
            <div>
              <ActionTitle $type="smallHeadline">
                Vertical Position (From top)
              </ActionTitle>
              <Input
                disabled={imagePosition !== 'custom'}
                value={imagePositionTop ?? ''}
                onChange={(e) => setImagePositionTop(parseInt(e.target.value))}
                placeholder="-536 px"
                type="number"
              />
            </div>
          </InputsGrid>
        </ModalContent>
        <SidePanelFooter $actions>
          <Button type="button" onClick={onUpdateSettings}>
            Apply
          </Button>
          <Button color="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
        </SidePanelFooter>
      </Dialog>
    </Modal>
  );
};

export default SettingsModal;
